import { useActivityDefinitions, usePlanDefinitions } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ActivityDefinition, PlanDefinition } from 'src/careflow/ph-core/fhir-client/resources/resources';
import { useMemo, useRef } from 'react';

/**
 * A generic hook factory for managing FHIR definitions (ActivityDefinition, PlanDefinition)
 * that share the same organization-based filtering and deduplication logic.
 */
const useDefinitionServiceBase = <T extends { name?: string; id?: string }>(
  useResourceHook: (options: { autofetch: boolean }) => [T[], any]
) => {
  const [, { find, create, update }] = useResourceHook({ autofetch: false });

  // Use a ref to always have access to the latest hook actions without triggering re-memos
  const actionsRef = useRef({ find, create, update });
  actionsRef.current = { find, create, update };

  return useMemo(
    () => ({
      getFiltered: async (organizations: string[]): Promise<T[]> => {
        const orgReferences = organizations.map((id) => `Organization/${id}`).join(',');
        const { find } = actionsRef.current;

        const [missingDefs, withOrgDefs] = await Promise.all([
          find({ _missing: 'use-context' }),
          orgReferences ? find({ 'use-context:value-reference': orgReferences }) : Promise.resolve([]),
        ]);

        // Deduplicate by name, prioritizing organization-specific definitions
        const deduplicated = new Map<string, T>();
        (withOrgDefs as T[]).forEach((def) => def.name && deduplicated.set(def.name, def));
        (missingDefs as T[]).forEach((def) => def.name && !deduplicated.has(def.name) && deduplicated.set(def.name, def));

        return Array.from(deduplicated.values());
      },

      create: async (data: T): Promise<T> => {
        const [result] = await actionsRef.current.create(data);
        return result as T;
      },

      update: async (id: string, data: T): Promise<T> => {
        const [result] = await actionsRef.current.update({ ...data, id });
        return result as T;
      },
    }),
    []
  );
};

export const useActivityDefinitionService = () =>
  useDefinitionServiceBase<ActivityDefinition>(useActivityDefinitions);

export const usePlanDefinitionService = () =>
  useDefinitionServiceBase<PlanDefinition>(usePlanDefinitions);